/**
 * Login Page Component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import * as authApi from '../services/authApi';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [organizations, setOrganizations] = useState<authApi.Organization[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  
  // Password change dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);

  // Show organization selector for PPAdmin (detect by username pattern)
  const isPPCandaAdminUsername = usernameOrEmail.toLowerCase().includes('ppadmin') ||
                              usernameOrEmail.toLowerCase().includes('ppcandaadmin') ||
                              usernameOrEmail.toLowerCase() === 'admin' ||
                              usernameOrEmail.toLowerCase().includes('admin@');
  
  // Load organizations for login page
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const result = await authApi.getPublicOrganizations();
        setOrganizations(result.organizations);
      } catch (error) {
        console.error('Failed to load organizations:', error);
        // Fallback to static list if API fails
        setOrganizations([
          { id: 'ppcanda', name: 'ppcanda', display_name: 'PPCANDA', theme: 'ppcanda' },
        ]);
      }
    };
    loadOrganizations();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!usernameOrEmail || !password) {
      setError('Please enter username/email and password');
      return;
    }

    setIsLoading(true);

    try {
      // Login directly with API to check must_change_password
      const response = await authApi.login(usernameOrEmail, password);
      
      // Check if password must be changed
      if (response.must_change_password) {
        // Store token temporarily for password change
        setTempToken(response.token);
        setShowPasswordDialog(true);
        setIsLoading(false);
        return;
      }
      
      // Normal login flow (no password change required)
      await login(usernameOrEmail, password, selectedOrganizationId || undefined);
      navigate('/training');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setPasswordError('Please enter both passwords');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (!tempToken) {
      setPasswordError('Authentication error. Please try logging in again.');
      return;
    }

    setIsChangingPassword(true);

    try {
      // Change password using the temporary token
      await authApi.changePassword(tempToken, password, newPassword);
      
      // Clear sensitive data
      setTempToken(null);
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordDialog(false);
      
      // Force logout (clear any stored tokens)
      await logout();
      
      // Show success message
      setError('');
      alert('Password changed successfully! Please log in with your new password.');
      
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = async () => {
    // User cancelled password change, force logout
    setShowPasswordDialog(false);
    setTempToken(null);
    setNewPassword('');
    setConfirmPassword('');
    setPassword('');
    await logout();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#DDE4E6',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="PPCANDA"
              sx={{ height: 80, mb: 2 }}
            />
            <Typography variant="h4" gutterBottom fontWeight={700}>
              PPCanda Design Automation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Electrical Diagrams - BOM Extraction
            </Typography>
          </Box>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username or Email"
              variant="outlined"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 2 }}
            />

            {/* Organization selector for PPCandaAdmin */}
            {isPPCandaAdminUsername && organizations.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={selectedOrganizationId}
                  onChange={(e) => setSelectedOrganizationId(e.target.value)}
                  label="Organization"
                  disabled={isLoading}
                >
                  <MenuItem value="">
                    <em>Select Organization</em>
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
              disabled={isLoading}
              sx={{ mb: 2 }}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink
                component={Link}
                to="/forgot-password"
                sx={{ textDecoration: 'none', fontSize: '0.875rem' }}
              >
                Forgot password?
              </MuiLink>
            </Box>
          </form>

          {/* Footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Powered by Kamna Ventures
            </Typography>
          </Box>
        </Paper>

        {/* Password Change Dialog */}
        <Dialog 
          open={showPasswordDialog} 
          onClose={() => {}} // Prevent closing by clicking outside
          maxWidth="sm" 
          fullWidth
          disableEscapeKeyDown // Prevent closing with ESC key
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={600}>
              Change Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You are using a temporary password. Please set a new password to continue.
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isChangingPassword}
                sx={{ mb: 2 }}
                autoFocus
                helperText="Password must be at least 8 characters long"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isChangingPassword}
                sx={{ mb: 2 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordChange();
                  }
                }}
              />
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCancelPasswordChange} 
              disabled={isChangingPassword}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordChange}
              variant="contained"
              disabled={isChangingPassword}
              startIcon={isChangingPassword ? <CircularProgress size={20} /> : null}
            >
              {isChangingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default LoginPage;

